import { useMemo } from 'react';

import {
  BrainReviewState,
  DoBoxEntryKind,
  InboxEntry,
} from '@this+that/models';

import type { BrainAssertionView } from '../../models/companyBrain';
import {
  BrainReviewReasonCode,
  extractBrainReviewReasonCodes,
} from '../../utils/doBox/extractBrainReviewReasonCodes';
import { BrainAssertionLoadState } from '../Brain/CompanyBrain/brainAssertionLoadState';
import { useBrainAssertion } from '../Brain/CompanyBrain/useBrainAssertion';

export interface BrainReviewEntryState {
  isBrainReview: boolean;
  assertion: BrainAssertionView | null;
  loadState: BrainAssertionLoadState;
  canRespond: boolean;
  reasonCodes: BrainReviewReasonCode[];
}

export function useBrainReviewEntry(
  inboxEntry: Pick<InboxEntry, 'entryKind' | 'brainReview' | 'description'>,
): BrainReviewEntryState {
  const isBrainReview = inboxEntry.entryKind === DoBoxEntryKind.BRAIN_REVIEW;
  const { assertion, loadState } = useBrainAssertion({
    teamId: inboxEntry.brainReview?.teamId ?? '',
    assertionId: inboxEntry.brainReview?.assertionId ?? '',
    skip: !isBrainReview,
  });
  const reasonCodes = useMemo(
    () =>
      isBrainReview
        ? extractBrainReviewReasonCodes(inboxEntry.description)
        : [],
    [isBrainReview, inboxEntry.description],
  );

  const canRespond =
    !isBrainReview ||
    (loadState === BrainAssertionLoadState.READY &&
      assertion?.reviewState === BrainReviewState.PENDING);

  return { isBrainReview, assertion, loadState, canRespond, reasonCodes };
}
